import type { FC, FormEvent, ReactNode } from 'react'
import type { TeamMember, TeamRole } from '@/types'

import { useState } from 'react'
import { TODAY } from '@/lib/date'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { EMAIL_PATTERN, FormField, fieldMessageId } from '@/components/dashboard/form-field'
import { ASSIGNABLE_ROLES } from '@/data/team'
import { ROLE_DESCRIPTIONS, ROLE_LABELS } from '@/constants/dashboard'

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog'

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select'

interface InviteMemberDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    seatsLeft: number
    isEmailTaken: (email: string) => boolean
    onInvite: (member: TeamMember) => void
}

const ROLE_ITEMS = ASSIGNABLE_ROLES.map((role) => ({ value: role, label: ROLE_LABELS[role] }))

export const InviteMemberDialog: FC<InviteMemberDialogProps> = ({ open, onOpenChange, seatsLeft, isEmailTaken, onInvite }): ReactNode => {
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [role, setRole] = useState<TeamRole>('support')
    const [message, setMessage] = useState('')
    const [errors, setErrors] = useState<{ email?: string, name?: string }>({})

    const reset = (): void => {
        setName('')
        setEmail('')
        setRole('support')
        setMessage('')
        setErrors({})
    }

    const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()

        const normalized = email.trim().toLowerCase()
        const next: { email?: string, name?: string } = {}

        if (!name.trim()) next.name = 'Nama wajib diisi supaya mudah dikenali tim.'

        if (!normalized) next.email = 'Email wajib diisi.'
        else if (!EMAIL_PATTERN.test(normalized)) next.email = 'Format email belum benar, contoh: nama@arunika.id'
        else if (isEmailTaken(normalized)) next.email = 'Orang ini sudah menjadi anggota atau sudah diundang.'

        setErrors(next)

        if (next.name) {
            document.getElementById('invite-name')?.focus()
            return
        }

        if (next.email) {
            document.getElementById('invite-email')?.focus()
            return
        }

        onInvite({
            id: `tm-${normalized}`,
            name: name.trim(),
            email: normalized,
            title: ROLE_LABELS[role],
            role,
            status: 'invited',
            lastActiveAt: null,
            invitedAt: TODAY
        })

        reset()
        onOpenChange(false)
    }

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                onOpenChange(next)
                if (!next) reset()
            }}
        >
            <DialogContent className='sm:max-w-md'>
                <form onSubmit={onSubmit} noValidate className='grid gap-5'>
                    <DialogHeader>
                        <DialogTitle>
                            Undang anggota tim
                        </DialogTitle>
                        <DialogDescription>
                            {seatsLeft > 0
                                ? `Masih ada ${seatsLeft} kursi kosong di workspace ini.`
                                : 'Semua kursi sudah terpakai. Hapus anggota atau undangan lain terlebih dahulu.'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className='grid gap-4'>
                        <FormField id='invite-name' label='Nama' required error={errors.name}>
                            <Input
                                id='invite-name'
                                value={name}
                                onChange={(event) => {
                                    setName(event.target.value)
                                    setErrors((current) => ({ ...current, name: undefined }))
                                }}
                                placeholder='Nama lengkap'
                                autoComplete='off'
                                aria-invalid={Boolean(errors.name)}
                                aria-describedby={errors.name ? fieldMessageId('invite-name') : undefined}
                            />
                        </FormField>

                        <FormField id='invite-email' label='Email kerja' required error={errors.email}>
                            <Input
                                id='invite-email'
                                type='email'
                                inputMode='email'
                                value={email}
                                onChange={(event) => {
                                    setEmail(event.target.value)
                                    setErrors((current) => ({ ...current, email: undefined }))
                                }}
                                placeholder='nama@arunika.id'
                                autoComplete='off'
                                aria-invalid={Boolean(errors.email)}
                                aria-describedby={errors.email ? fieldMessageId('invite-email') : undefined}
                            />
                        </FormField>

                        <FormField id='invite-role' label='Peran' hint={ROLE_DESCRIPTIONS[role]}>
                            <Select
                                items={ROLE_ITEMS}
                                value={role}
                                onValueChange={(value) => {
                                    if (value) setRole(value as TeamRole)
                                }}
                            >
                                <SelectTrigger id='invite-role' className='w-full' aria-describedby={fieldMessageId('invite-role')}>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {ROLE_ITEMS.map((item) => (
                                        <SelectItem key={item.value} value={item.value}>
                                            {item.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FormField>

                        <FormField id='invite-message' label='Pesan pribadi' hint='Opsional, ikut ditampilkan di undangan.'>
                            <Textarea
                                id='invite-message'
                                value={message}
                                onChange={(event) => setMessage(event.target.value)}
                                placeholder='Halo, yuk bergabung ke workspace Arunika.'
                                maxLength={280}
                                aria-describedby={fieldMessageId('invite-message')}
                            />
                        </FormField>
                    </div>

                    <DialogFooter>
                        <DialogClose render={<Button type='button' variant='outline' />}>
                            Batal
                        </DialogClose>
                        <Button type='submit' disabled={seatsLeft <= 0}>
                            Buat undangan
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}
